import type { CharacterChoice } from '../characters/characterSelection';
import type { UiConfig } from '../config/ui.config';
import type { RunSummary } from '../core/GameEvents';
import { CharacterSelect } from './CharacterSelect';
import { el, formatNumber } from './dom';
import { HowToScreen } from './HowToScreen';
import type { LeaderboardEntry, OwnLeaderboardEntry } from './Leaderboard';
import { LeaderboardScreen } from './LeaderboardScreen';
import { LoadingScreen } from './LoadingScreen';
import { MenuBackdrop } from './MenuParts';
import type { SoundMode } from '../services/ProgressStore';
import { MusicToggle } from './MusicToggle';
import { RegisterScreen } from './RegisterScreen';
import { StartScreen } from './StartScreen';
import type { AdSponsor } from '../advertising/AdProvider';

export interface ScreenActions {
  readonly onStart: () => void;
  readonly onResume: () => void;
  readonly onRestart: () => void;
  /** Pause / game over: abort the run and go back to the start screen. */
  readonly onHome: () => void;
  readonly onToggleMusic: () => void;
  /** A character card / figure was clicked or tapped (start and game-over screen). */
  readonly onSelectCharacter: (id: string) => void;
  /** Arrow buttons of the start screen: previous (-1) / next (+1) character. */
  readonly onStepCharacter: (direction: -1 | 1) => void;
  /** Leaderboard page, signed in: "Abmelden" (forget the Spielausweis on this device). */
  readonly onSignOut: () => void;
}

const DEATH_TEXT: Readonly<Record<RunSummary['reason'], string>> = {
  HIT: 'Voll gegen das Hindernis!',
  CAUGHT: 'Eingeholt – der Bauherr hat dich!',
  FELL: 'Abgestürzt!',
  MISSED_TURN: 'Abzweigung verpasst!',
};

/**
 * Loading, start, leaderboard, sign-up, how-to, pause and game-over overlays. The menu screens (loading, start,
 * leaderboard, sign-up, how-to) sit on the opaque menu backdrop (the 3D scene is hidden); pause and game over dim
 * the scene. Leaderboard, sign-up and how-to are pages of the start screen – opening them changes no game state.
 */
export class Screens {
  private readonly backdrop: MenuBackdrop;
  private readonly loading: LoadingScreen;
  private readonly start: StartScreen;
  private readonly leaderboard: LeaderboardScreen;
  private readonly register: RegisterScreen;
  private readonly howTo: HowToScreen;
  private readonly pause: HTMLDivElement;
  private readonly pauseMusic: MusicToggle;
  private readonly gameOver: HTMLDivElement;
  private readonly gameOverStats: HTMLDivElement;
  private readonly gameOverReason: HTMLParagraphElement;
  private readonly gameOverSelect: CharacterSelect | null = null;
  private readonly recordButton: HTMLButtonElement;
  /** Signed in: instead of "Anmelden & mitspielen". */
  private readonly leaderboardButton: HTMLButtonElement;
  /** Signed in: "Platz 3 in der Bestenliste" after the report of the run. */
  private readonly recordLine: HTMLParagraphElement;
  private readonly menuScreens: readonly HTMLDivElement[];

  constructor(parent: HTMLElement, actions: ScreenActions, characters: readonly CharacterChoice[], ui: UiConfig, selectedId: string) {
    this.backdrop = new MenuBackdrop(parent, ui.backdrop);
    const selected = characters.find((choice) => choice.id === selectedId) ?? characters[0];
    this.loading = new LoadingScreen(parent, ui, selected?.art ?? null);
    this.start = new StartScreen(parent, ui, characters, {
      onStart: actions.onStart,
      onSelect: actions.onSelectCharacter,
      onStep: actions.onStepCharacter,
      onShowLeaderboard: () => this.showLeaderboard(),
      onShowHowTo: () => this.showHowTo(),
      onToggleMusic: actions.onToggleMusic,
    });
    this.leaderboard = new LeaderboardScreen(parent, ui, { onRegister: () => this.showRegister(), onBack: () => this.showStart(), onSignOut: actions.onSignOut });
    this.register = new RegisterScreen(parent, ui, () => this.showStart());
    this.howTo = new HowToScreen(parent, ui, characters, () => this.showStart());
    this.menuScreens = [this.loading.element, this.start.element, this.leaderboard.element, this.register.element, this.howTo.element];

    this.pause = this.overlay(parent, 'screen-pause');
    el('div', 'screen-title', 'Pause', this.pause);
    this.button(this.pause, 'Weiter', actions.onResume, 'ESC');
    this.button(this.pause, 'Neu starten', actions.onRestart, 'R', 'is-secondary');
    this.button(this.pause, 'Zur Startseite', actions.onHome, 'Lauf abbrechen', 'is-secondary');
    this.pauseMusic = new MusicToggle(this.pause, 'screen-music', actions.onToggleMusic, true);

    this.gameOver = this.overlay(parent, 'screen-gameover');
    el('div', 'screen-title is-danger', 'GAME OVER', this.gameOver);
    this.gameOverReason = el('p', 'screen-subtitle', '', this.gameOver);
    this.gameOverStats = el('div', 'screen-stats', undefined, this.gameOver);
    // user 2026-10-03: "Registrieren und Rekord eintragen" under the stats – the run is over, so back to the start
    // screen first (no game state stays behind the sign-up page), then the sign-up page on top. Signed in (ructec
    // 2026-10-08): instead the place in the leaderboard – the run was reported automatically.
    this.recordButton = this.button(this.gameOver, 'Anmelden & mitspielen', () => {
      actions.onHome();
      this.showRegister();
    }, 'mit dem ructec-Konto deines Unternehmens in die Bestenliste', 'is-secondary screen-record');
    this.recordLine = el('p', 'screen-text screen-record-line', '', this.gameOver);
    this.recordLine.hidden = true;
    this.leaderboardButton = this.button(this.gameOver, 'Bestenliste ansehen', () => {
      actions.onHome();
      this.showLeaderboard();
    }, 'dein Platz mit Punkten und Metern', 'is-secondary screen-record');
    this.leaderboardButton.hidden = true;
    if (characters.length >= 2) {
      this.gameOverSelect = new CharacterSelect(this.gameOver, characters, actions.onSelectCharacter, 'is-compact');
      el('p', 'screen-text char-select-hint screen-key-hint', '← / → Figur wechseln', this.gameOver);
      el('p', 'screen-text char-select-hint screen-touch-hint', 'Figur antippen zum Wechseln', this.gameOver);
    }
    this.button(this.gameOver, 'Nochmal', actions.onRestart, 'Leertaste / R');
    this.button(this.gameOver, 'Zur Startseite', actions.onHome, 'Bestenliste, Figur, Musik', 'is-secondary');
  }

  /** True while the opaque menu backdrop hides the 3D scene (loading, start and its pages). */
  get coversScene(): boolean {
    return this.backdrop.visible;
  }

  /** True while the start screen itself shows – not one of its pages (leaderboard, sign-up). */
  get startVisible(): boolean {
    return this.start.element.classList.contains('is-visible');
  }

  /** Highlights the chosen character on every selection. */
  setSelectedCharacter(id: string): void {
    this.start.setSelected(id);
    this.gameOverSelect?.setSelected(id);
  }

  setSoundMode(mode: SoundMode): void {
    this.start.setSoundMode(mode);
    this.pauseMusic.setMode(mode);
  }

  /** "sponsored by" on the start screen (company of a running booking, or none). */
  setSponsor(sponsor: AdSponsor | null): void {
    this.start.setSponsor(sponsor);
  }

  /** Personal best of this device – start-screen preview and leaderboard page. */
  setBestScore(score: number): void {
    this.start.setBestScore(score);
    this.leaderboard.setBestScore(score);
  }

  /**
   * Leaderboard rows (server-side leaderboard, follows) – empty: every place shows as free. `own` is the signed-in
   * player's place: highlighted where it stands, below the list with its rank if it is not among the shown places.
   */
  setLeaderboard(entries: readonly LeaderboardEntry[], own: OwnLeaderboardEntry | null = null): void {
    this.start.setLeaderboard(entries, own);
    this.leaderboard.setEntries(entries, own);
  }

  /** Signed in for the leaderboard (Spielausweis from ructec.com) – and as whom, once the server told. */
  setLeaderboardSignedIn(signedIn: boolean, player: { readonly company: string; readonly name: string } | null): void {
    this.leaderboard.setSignedIn(signedIn, player);
    this.start.setSignedIn(signedIn, player);
    this.recordButton.hidden = signedIn;
    this.leaderboardButton.hidden = !signedIn;
    if (!signedIn) this.recordLine.hidden = true;
  }

  /** Game over, signed in: the run was reported – show its place (or that it is being entered / failed). */
  setRunRanking(text: string | null): void {
    this.recordLine.textContent = text ?? '';
    this.recordLine.hidden = !text;
  }

  /** Asset preload progress, 0..1. */
  setLoadingProgress(fraction: number): void {
    this.loading.setProgress(fraction);
  }

  showLoading(): void {
    this.showOnly(this.loading.element);
  }

  showStart(): void {
    this.showOnly(this.start.element);
  }

  showLeaderboard(): void {
    this.showOnly(this.leaderboard.element);
  }

  showRegister(): void {
    this.showOnly(this.register.element);
  }

  /** "So funktioniert’s" – opens at its top every time. */
  showHowTo(): void {
    this.howTo.scrollToTop();
    this.showOnly(this.howTo.element);
  }

  showPause(): void {
    this.showOnly(this.pause);
  }

  showGameOver(summary: RunSummary, best: number, isNewBest: boolean): void {
    this.gameOverReason.textContent = DEATH_TEXT[summary.reason];
    const rows: [string, string][] = [
      ['Punkte', formatNumber(summary.score)],
      ['Distanz', `${formatNumber(summary.distance)} m`],
      ['Bierflaschen', formatNumber(summary.pickups.bottles)],
      ['Rekord', `${formatNumber(best)}${isNewBest ? '  NEU!' : ''}`],
    ];
    this.gameOverStats.replaceChildren(
      ...rows.map(([label, value]) => {
        const row = el('div', 'screen-stat');
        el('span', undefined, label, row);
        el('strong', undefined, value, row);
        return row;
      }),
    );
    this.showOnly(this.gameOver);
  }

  hideAll(): void {
    this.showOnly(null);
  }

  private showOnly(screen: HTMLDivElement | null): void {
    for (const s of [...this.menuScreens, this.pause, this.gameOver]) s.classList.toggle('is-visible', s === screen);
    this.backdrop.setVisible(screen !== null && this.menuScreens.includes(screen));
  }

  private overlay(parent: HTMLElement, className: string): HTMLDivElement {
    return el('div', `screen ${className}`, undefined, parent);
  }

  private button(parent: HTMLElement, label: string, onClick: () => void, hint: string, extra = ''): HTMLButtonElement {
    const button = el('button', `screen-button ${extra}`.trim(), undefined, parent);
    button.type = 'button';
    el('span', undefined, label, button);
    el('small', undefined, hint, button);
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      onClick();
    });
    // Touch: the button handles the tap; it must not also count as a swipe/tap on the canvas.
    button.addEventListener('pointerdown', (event) => event.stopPropagation());
    return button;
  }
}
